import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';

import { FormControl } from '@angular/forms';

import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete/autocomplete';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { Suggestions } from '../entities/suggestions.entity';

export type Option<S> = {
	disabled: boolean;
	class: string | null;
	value: S | null;
	label: string;
}

export type CategoryBuilder<P, S> = {
	titleGetter: (possibilities: P) => string | null;
	suggestionsFilter: (suggestion: S) => boolean;
};

@Component({
	selector: 'suggestable-input',
	templateUrl: 'suggestable-input.html',
	styleUrls: ['suggestable-input.css']
})
export class SuggestableInputComponent<P, S> {
	// Le composant de saisie, vu comme un FormControl, pour manipulation de son contenu
	inputControl = new FormControl();
	
	// Derniere valeur textuelle connue
	private textValue: string = '';
		
	// Categories de suggestions a afficher
	optionsClassic: Option<S>[] = [];
	optionsHtml: Option<S>[] = [];
	
	// Indicateur permettant de desactiver l'autocompletion
	disableAutocomplete: boolean = false;
	
	// Message permettant en interne de jouer la logique de chargement de suggestions
	loadSuggestionsSubject = new Subject<{ value: string | null | undefined | S, forceDisplay: boolean}>();
	
	// Relais entre inputLabelGetter et le displayWith pour ajouter la gestion des values (string) injectees par le parent 
	displayWith = (suggestion: S | string | null | undefined) => {
		if (suggestion === null || suggestion === undefined) {
			return '';
		}
		
		if (typeof suggestion === "string") {
			return suggestion;
		}

		return this.inputLabelGetter(suggestion);
	};

	constructor() {
		let toTextValue = (value: string | S | null | undefined) => {
			if (!value) {
				return '';
			}
			
			if (typeof value === 'string')
				return value;

			// Si on arrive a ce point, c'est que l'utilisateur a clique sur une suggestion
			// Dans ce cas, l'inputControl renvoie l'entite representant la selection
			// Il faut la convertir en valeur textuelle pour la suite
			return this.inputLabelGetter(value);
		}
		
		this.loadSuggestionsSubject.pipe(
			// Convertir la valeur en valeur textuelle
			map (
				({value, forceDisplay}) => {
					let textValue = toTextValue(value);
					return { textValue: textValue, forceDisplay: forceDisplay };
				}
			),
			// Si la valeur textuelle n'a pas ete modifiee, s'interrompre ici sauf si on doit forcer l'affichage
			filter(
				({textValue, forceDisplay}) => forceDisplay || (this.textValue !== textValue)
			),
			// Jeter l'information dont on a plus besoin, par souci de simplicite
			map(
				({textValue}) => textValue
			),
			// Si on est pas dans un cas ou l'affichage est inutile
			tap(
				(textValue) => {
					// Vider les anciennes suggestions
					this.optionsClassic = [];
					this.optionsHtml = [];

					// Notifier le parent du changement de cette valeur
					this.valueChange.emit(textValue);
					
					// Memoriser la valeur textuelle pour pouvoir detecter les changement ulterieurs 
					this.textValue = textValue;
				}
			),
			// Ne pas aller plus loin s'il n'y a pas au moins deux caracteres saisis
			filter(
				(textValue) => {
					return textValue.length >= this.minimumSearchInput;
				}
			),
			// Sinon placer afficher le panneau de suggestions et l'indicateur de chargement
			tap(
				(_textValue) => {
					this.optionsClassic = [this.newInfoOption("Chargement des suggestions...")];
					this.optionsHtml = [];
				}			
			),
			// Puis la fonction serveur pour obtenir les resultats
			switchMap(
				(textValue) => this.suggestionsObservableGetter(textValue).pipe(
					// Fusionner les resultats avec la valeur de saisie avant transmission
					map(
						(data: Suggestions<P, S>) =>  {
							return { loadedData: data, inputValue: textValue };
						}
					)
				)
			)
		)
		// A la reception des suggestions liees a une nouvelle valeur
		.subscribe(
			(data) => {
				// Evaluation des categories a afficher
				let options = [] as Option<S>[];
				this.categoryBuilders.forEach(
					(builder) => {
						let infoLabel = builder.titleGetter(data.loadedData.possibles);
						if (infoLabel) {
							let infoOption = this.newInfoOption(infoLabel);
							options.push(infoOption);
						}
						
						data.loadedData.suggestions
							.filter(builder.suggestionsFilter)
							.forEach(
								(suggestion) => {
									let label = this.labelGetter(suggestion);
									let valueOption = this.newValueOption(suggestion, label);
									options.push(valueOption);
								}
							)
						;
					}
				);
				
				// Binder les valeur sur la vue
				if (this.labelAsHtml) {
					this.optionsClassic = [];
					this.optionsHtml = options;
				} else {
					this.optionsClassic = options;
					this.optionsHtml = [];
				}
			}
		);

		// Met en place la liaison de donnee entre la valeur saisie dans le champs la commande de chargement des suggestions
		this.inputControl.valueChanges.pipe(
			// Ignorer les saisies qui ont lieu a moins de 500ms d'intervale
			debounceTime(500)
		).subscribe(
			(value) => this.loadSuggestionsSubject.next({ value: value, forceDisplay: false})
		);
	}
	
	private newInfoOption(label: string): Option<S> {
		return {
			disabled: true,
			class: 'info-option',
			value: null,
			label: label,
		};
	}

	private newValueOption(suggestion: S, label: string): Option<S> {
		return {
			disabled: false,
			class: 'value-option',
			value: suggestion,
			label: label,
		};
	}

	// Le composant de saisie, vu comme un trigger pour la manipulation de la dropdown list
	@ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) input!: MatAutocompleteTrigger;

	// La class de style a appliquer au composant de saisie
	@Input() inputClass: string = '';

	@Input() maxlength: string | number | null = 200;

	// L'indicateur de desactivation du composant
	@Input() disabled: boolean = false;

	// Classe(s) a ajouter au panneau de suggestions
	@Input() panelClass: string | string[] = [];

	// Fonction retournant une promesse de reponse pour les suggestions en fonction d'une saisie
	@Input() suggestionsObservableGetter!: (input: string) => Observable<Suggestions<P, S>>;
	
	@Input() placeholder: string | null = null;

	@Input() maxLength: number | null = null;
	
	@Input() minimumSearchInput: number = 2;

	// Regles de fabrication de categories
	@Input() categoryBuilders: CategoryBuilder<P, S>[] = [
		{
			titleGetter: (possibles: P) => "Correspondances possibles: " + possibles,
			suggestionsFilter: (_suggestion: S) => true,
		}
	];

	// La fonction a utiliser pour obtenir le libelle d'un item (si pas fourni on estime que les items suggeres sont deja des strings)
	@Input() labelGetter: (suggestion: S) => string = (suggestion: S) => '' + suggestion;

	// Indique si le labelGetter renvoie du Html ou non
	@Input() labelAsHtml: boolean = false;

	// Convertit la valeur cliquee en valeur pour l'input
	@Input() inputLabelGetter = (suggestion: S) => this.labelGetter(suggestion);

	// La valeur actuellement saisie dans le champ, en biding reactif pour pouvoir reagir aux changements venant du parent
	@Input() set value(value: string | null | undefined) {
		if (value !== this.textValue) {
			// Quand on recoit une nouvelle valeur textuelle du parent, il faut l'injecter dans le champ input
			this.inputControl.setValue(value);

			// NB: Ceci doit normalement provoquer la mise a jour de la propriete this.textValue
		}
	}
	get value(): string | null | undefined {
		return this.textValue;
	}

	// L'evenement necessaire au two-way binding pour la valeur
	@Output() valueChange = new EventEmitter<any>();
	
	// Evenement declenche quand l'utilisateur selectionne une valeur de la liste
	@Output() valueSelected = new EventEmitter<S | undefined>();

	onInputFocus() {
		// Lorsque le focus arrive sur le composant, on reinitialise la liste filtree.
		// En effet, le contenu de la liste source peut avoir change meme si sa reference est identique

		// Recuperer la valeur du champ de saisie
		let value = this.inputControl.value;

		// Updater les suggestions
		this.loadSuggestionsSubject.next({ value: value, forceDisplay: true});
	}

	onFocusOut(_event: any) {
		// lorsqu'on quitte le champ
		// si on a vidé le champs texte, on le signale au conteneur appelant
		if (!this.inputControl.value) {
			this.valueChange.emit('');
		}
	}
	
	onOptionSelected(event: MatAutocompleteSelectedEvent) {
		let suggestion: S | undefined = event.option.value as S;
		this.valueSelected.emit(suggestion);
	}
}